/**
 * Employee multi-selection component for shift forms
 * Allows selecting multiple employees using EnhancedMultiSelect with avatars
 */

import * as React from 'react';
import { useLingui } from '@lingui/react';
import { EnhancedMultiSelect } from '@/components/ui/enhanced-multiselect';
import type { EnhancedSelectOption } from '@/components/ui/enhanced-multiselect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { isSelectableEmployee, useAllEmployees } from '@/hooks/useEmployees';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { Loader2 } from 'lucide-react';

export interface EmployeeMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
}

interface EmployeeData {
  id?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
}

/**
 * Helper component to render employee avatar with image URL resolution
 */
function EmployeeAvatar({ employee }: { employee: EmployeeData }) {
  const avatarUrlRaw = employee.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;

  const needsImageResolution = isImageId(avatarUrl);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined;
  }

  const getInitials = (firstname?: string, lastname?: string) => {
    const firstInitial = firstname?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastname?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const initials = getInitials(employee.firstname, employee.lastname);

  return (
    <Avatar className="h-6 w-6 flex-shrink-0">
      {finalAvatarUrl && (
        <AvatarImage
          src={finalAvatarUrl}
          alt={`${employee.firstname} ${employee.lastname}`}
        />
      )}
      <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * A multi-select component for choosing multiple employees.
 * Self-contained: fetches employees via useAllEmployees and displays them
 * in a searchable multi-select dropdown with avatars.
 */
export function EmployeeMultiSelect({
  value,
  onChange,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
}: EmployeeMultiSelectProps) {
  const { i18n } = useLingui();
  const { data: employeeList, isLoading, isError } = useAllEmployees();

  const options: EnhancedSelectOption[] = React.useMemo(() => {
    const employees = employeeList || [];
    // Departed employees are not offered (#1688); already selected ones stay.
    return employees
      .filter((emp) => emp.id && emp.id.trim() !== '')
      .filter((emp) => value.includes(emp.id as string) || isSelectableEmployee(emp))
      .map((employee) => ({
        value: employee.id || '',
        label: `${employee.firstname} ${employee.lastname}`,
      }));
  }, [employeeList, value]);

  const employeeMap = React.useMemo(() => {
    const employees = employeeList || [];
    const map = new Map<string, EmployeeData>();
    employees.forEach((employee) => {
      if (employee.id) {
        map.set(employee.id, employee);
      }
    });
    return map;
  }, [employeeList]);

  const renderEmployeeOption = React.useCallback(
    (option: EnhancedSelectOption) => {
      const employee = employeeMap.get(option.value);
      if (!employee) {
        return <span>{option.label}</span>;
      }

      return (
        <div className="flex items-center gap-2">
          <EmployeeAvatar employee={employee} />
          <span>{option.label}</span>
        </div>
      );
    },
    [employeeMap]
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-muted-foreground text-sm">
          {i18n._({ id: 'common.loading', message: 'Loading...' })}
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center h-10 px-3 border border-destructive rounded-md bg-destructive/10">
        <span className="text-destructive text-sm">
          {i18n._({ id: 'shifts.form.employeeLoadError', message: 'Failed to load employees' })}
        </span>
      </div>
    );
  }

  return (
    <EnhancedMultiSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={i18n._({
        id: 'shifts.copyWeek.filterEmployees',
        message: 'Filter by employees',
      })}
      disabled={disabled}
      id={id}
      aria-describedby={ariaDescribedBy}
      renderOption={renderEmployeeOption}
    />
  );
}
